import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VideoModalComponent } from '../../core/components/video-modal/video-modal.component';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { ImageCarouselComponent } from '../../core/components/image-carousel/image-carousel.component';
import { BackendService } from '../../core/services/backend.service';
import { Project } from '../../core/interfaces/Project';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { preferred_lang } from '../../core/signals/translation.signal';



@Component({
  selector: 'app-home',
  imports: [CommonModule, MenuBarComponent, FooterComponent, ImageCarouselComponent, NgxTolgeeModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  animations: [
    // trigger('fadeImage', [
    //   transition('* => *', [
    //     style({ opacity: 0, transform: 'scale(0.97)' }),
    //     animate(
    //       '600ms cubic-bezier(0.22, 1, 0.36, 1)',
    //       style({ opacity: 1, transform: 'scale(1)' })
    //     )
    //   ])
    // ])
    trigger('fadeImage', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('800ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
  ]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabsSection', { static: true }) tabsSection!: ElementRef;
  // title/subtitle hold translation keys, resolved with | translate in the template
  tabs: any[] = [
    {
      title: 'home_tab_home_title',
      subtitle: 'home_tab_home_subtitle',
      images: ['/assets/images/homepage/home1.png', 'assets/images/homepage/home2.png', 'assets/images/homepage/home3.png', 'assets/images/homepage/home4.png'],
      color: '#0FB9B1',
      route: 'solution/home',
    },
    {
      title: 'home_tab_hotels_title',
      subtitle: 'home_tab_hotels_subtitle',
      images: ['assets/images/homepage/hotel1.png', 'assets/images/homepage/hotel2.png', 'assets/images/homepage/hotel3.png', 'assets/images/homepage/hotel4.png'],
      color: '#F368E0',
      route: 'solution/hotel',
    },
    {
      title: 'home_tab_workspaces_title',
      subtitle: 'home_tab_workspaces_subtitle',
      images: ['assets/images/homepage/workspace1.png', 'assets/images/homepage/workspace2.png', 'assets/images/homepage/workspace3.png', 'assets/images/homepage/workspace4.png'],
      color: '#6C5CE7',
      route: 'solution/workspace',
    }
  ];
  isMobile = window.innerWidth <= 768;
  // Variables for image carousel on tabs section
  activeIndex = 0;
  activeImageIndex = 0;
  private carouselInterval!: ReturnType<typeof setInterval>;
  private observer!: IntersectionObserver;
  private videoObserver!: IntersectionObserver;
  readonly carouselDelay = 5500; // 5.5 seconds
  private sectionVisible = false;

  // Variables for project carousel
  isAtStart = true;
  isAtEnd = false;
  @ViewChild('carouselWrapper', { static: true }) carouselWrapper!: ElementRef;
  carouselItems: Project[] = [];

  // Hover effect variables for section 1
  maskX = '-9999px';
  maskY = '-9999px';


  constructor(private router: Router, private el: ElementRef, private backendService: BackendService, private route: ActivatedRoute, private modalService: NgbModal) {
    // Project copy is localised server-side, so switching language has to
    // re-fetch it — Tolgee only swaps the static strings. Reading the signal
    // here is what subscribes this effect to it; it also covers the first load.
    effect(() => {
      preferred_lang();
      this.backendService.getProjects().subscribe(items => {
        this.carouselItems = items.data;
      });
    });
  }

  ngOnInit() {

    this.route.queryParams.subscribe(params => {
      const sectionId = params['scrollTo'];
      if (sectionId) {
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    });
  }

  ngOnDestroy() {
    this.stopImageCarousel();
    if (this.observer) this.observer.disconnect();
    if (this.videoObserver) this.videoObserver.disconnect();
  }

  get activeTab() {
    return this.tabs[this.activeIndex];
  }

  //Image carousel on tabs section
  startImageCarousel() {
    this.stopImageCarousel();

    const currentTab = this.tabs[this.activeIndex];
    if (!currentTab?.images?.length) return;

    this.carouselInterval = setInterval(() => {
      this.activeImageIndex = (this.activeImageIndex + 1) % currentTab.images.length;
    }, this.carouselDelay);
  }

  stopImageCarousel() {
    if (this.carouselInterval) clearInterval(this.carouselInterval);
  }

  onTabClick(index: number) {
    this.activeIndex = index;
    this.activeImageIndex = 0; //reset only on tab change
    this.startImageCarousel();
  }

  onDotClick(index: number) {
    this.activeImageIndex = index;
    this.startImageCarousel(); // restart the interval timer
  }

  //Hover coloring effect on section 1
  onMouseMove(event: MouseEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.maskX = `${event.clientX - rect.left}px`;
    this.maskY = `${event.clientY - rect.top}px`;
  }

  hideHoverEffect() {
    this.maskX = '-9999px';
    this.maskY = '-9999px';
  }

  ngAfterViewInit(): void {
    const cards = this.el.nativeElement.querySelectorAll('.card');

    // Cards glow effect
    window.addEventListener('mousemove', (ev: MouseEvent) => {
      cards.forEach((card: HTMLElement) => {
        const blob = card.querySelector('.blob') as HTMLElement;
        const fblob = card.querySelector('.fakeblob') as HTMLElement;
        const rect = fblob.getBoundingClientRect();

        // Animate glow position for each card
        blob.animate(
          [
            {
              transform: `translate(${ev.clientX - rect.left - rect.width / 2}px, ${ev.clientY - rect.top - rect.height / 2
                }px)`
            }
          ],
          { duration: 300, fill: 'forwards' }
        );

        // Calculate cursor distance to the card center to fade the glow naturally
        const dx = ev.clientX - (rect.left + rect.width / 2);
        const dy = ev.clientY - (rect.top + rect.height / 2);
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDist = Math.max(window.innerWidth, window.innerHeight) / 2;

        const opacity = 1 - Math.min(distance / maxDist, 1);
        blob.style.opacity = `${opacity}`;
      });
    });

    //Start image carousel on tab section only if the area is visible
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !this.sectionVisible) {
          this.sectionVisible = true;
          this.startImageCarousel();
        }
      },
      { threshold: 0.3 } // start when 30% of section is visible
    );

    this.observer.observe(this.tabsSection.nativeElement);

    // Play each video only when it enters the viewport
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    const videos = this.el.nativeElement.querySelectorAll('.bg-video');
    videos.forEach((video: HTMLVideoElement) => this.videoObserver.observe(video));
  }

  goToProject(projectTitle: string) {
    this.router.navigate(['/projects', projectTitle]);
  }

  goToUrl(page: string) {
    this.router.navigate([`/${page}`]);
  }

  openHowItWorks() {
    const ref = this.modalService.open(VideoModalComponent, {
      centered: true,
      windowClass: 'video-modal-window',
    });
    ref.componentInstance.videoId = 'KRiRu1AHsvI';
    ref.componentInstance.title = 'How it works';
  }

}
